/**
 * TOUCH CONTROLS — on-screen stick, look drag and action buttons.
 *
 * Design notes
 * ------------
 * - Injected through `input.setTouch*`: the touch layer emits *actions*
 *   ('fire', 'jump', …), never key codes, so keyboard, mouse, gamepad and
 *   touch all funnel into the same `Input` snapshot. Nothing here is read
 *   directly by gameplay.
 * - The move stick is floating: press anywhere on the left half and it appears
 *   under your thumb, which is what modern mobile shooters do and it means the
 *   player never has to hunt for a fixed pad.
 * - Look drag owns the right half but sits *under* the buttons, so a thumb
 *   landing on FIRE does not also swing the camera.
 * - Multi-touch is tracked by pointerId, so moving, looking and firing all
 *   work at the same time.
 * - Enabled only on coarse-pointer devices (phones/tablets). A desktop that
 *   reports touch as well gets nothing: the HUD would just be in the way.
 *
 *   ctx.get('touch') -> { dispose(), visible }
 */
const STYLE_ID = 'ducs-touch-style';

const CSS = `
.du-touch{position:fixed;inset:0;z-index:60;touch-action:none;user-select:none;
  -webkit-user-select:none;-webkit-tap-highlight-color:transparent;display:none}
.du-touch.on{display:block}
.du-stick{position:absolute;width:132px;height:132px;border-radius:50%;
  border:2px solid rgba(255,255,255,.22);background:rgba(255,255,255,.05);
  transform:translate(-50%,-50%);opacity:0;transition:opacity .12s;pointer-events:none}
.du-stick.on{opacity:1}
.du-knob{position:absolute;left:50%;top:50%;width:58px;height:58px;border-radius:50%;
  background:rgba(255,255,255,.28);border:2px solid rgba(255,255,255,.5);
  transform:translate(-50%,-50%)}
.du-look{position:absolute;right:0;top:0;bottom:0;left:38%;touch-action:none}
.du-btns{position:absolute;right:0;bottom:0;padding:0 18px 20px 0;
  display:flex;align-items:flex-end;gap:10px;pointer-events:none}
.du-btn{pointer-events:auto;touch-action:none;border-radius:50%;
  border:2px solid rgba(255,255,255,.3);background:rgba(255,255,255,.1);
  color:#e8eef6;font:600 13px/1 system-ui,sans-serif;letter-spacing:.04em;
  display:flex;align-items:center;justify-content:center}
.du-btn.press{background:rgba(255,255,255,.32);border-color:rgba(255,255,255,.7)}
.du-btn-fire{width:92px;height:92px;font-size:15px;background:rgba(255,90,70,.28);
  border-color:rgba(255,140,120,.55)}
.du-btn-m{width:60px;height:60px}
.du-btn-s{width:48px;height:48px}
.du-col{display:flex;flex-direction:column;gap:10px;align-items:center}
@media (orientation:portrait){
  .du-look{left:0}
  .du-btn-fire{width:78px;height:78px}
  .du-btn-m{width:52px;height:52px}
}
`;

/** Radius in CSS pixels at which the stick saturates. */
const STICK_R = 46;
/** Look sensitivity multiplier for touch (a thumb travels further than a mouse). */
const LOOK_SCALE = 1.15;

export class TouchControls {
  static id = 'touch';
  // No subsystem deps: `input` lives on ctx (engine-owned), and the DOM is
  // already there. Declaring a dep on 'input' would fail dependency resolution.
  static deps = [];

  constructor() {
    this.ctx = null;
    this.input = null;
    this.enabled = false;
    this.disposed = false;

    /** pointerId -> 'stick' | 'look' */
    this._roles = new Map();
    this._stickOrigin = { x: 0, y: 0 };
    this._lookLast = { x: 0, y: 0 };
    this._stickId = null;
    this._lookId = null;

    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onCancel = this._onCancel.bind(this);
  }

  async init(ctx) {
    this.ctx = ctx;
    this.input = ctx.input;
    // Desktop: keyboard and mouse already work, the pad would just be clutter.
    if (!matchMedia('(hover: none) and (pointer: coarse)').matches) return this;
    this.enabled = true;
    this._build();
    return this;
  }

  _build() {
    if (!document.getElementById(STYLE_ID)) {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }

    const host = document.getElementById('ui') ?? document.body;
    this.root = document.createElement('div');
    this.root.className = 'du-touch on';

    this.stick = document.createElement('div');
    this.stick.className = 'du-stick';
    this.knob = document.createElement('div');
    this.knob.className = 'du-knob';
    this.stick.appendChild(this.knob);

    // Look area sits under the buttons in DOM order.
    this.look = document.createElement('div');
    this.look.className = 'du-look';

    this.btnHost = document.createElement('div');
    this.btnHost.className = 'du-btns';
    // Small buttons above, big fire button nearest the thumb.
    const col = document.createElement('div');
    col.className = 'du-col';
    this._mkBtn(col, 'R', 'reload', 's');
    this._mkBtn(col, '跳', 'jump', 's');
    this._mkBtn(col, '蹲', 'crouch', 's');
    const col2 = document.createElement('div');
    col2.className = 'du-col';
    this._mkBtn(col2, '瞄', 'ads', 'm');
    this._mkBtn(col2, '刀', 'melee', 'm');
    const fire = this._mkBtn(null, 'FIRE', 'fire', 'fire');

    this.btnHost.append(col, col2, fire);
    this.root.append(this.look, this.stick, this.btnHost);
    host.appendChild(this.root);

    this.root.addEventListener('pointerdown', this._onDown);
    this.root.addEventListener('pointermove', this._onMove);
    addEventListener('pointerup', this._onUp);
    addEventListener('pointercancel', this._onCancel);
  }

  _mkBtn(parent, label, action, size) {
    const b = document.createElement('div');
    b.className = `du-btn du-btn-${size}`;
    b.textContent = label;
    b.dataset.act = action;
    if (parent) parent.appendChild(b);
    return b;
  }

  /* ─────────────────────────── pointer plumbing ─────────────────────────── */

  _onDown(e) {
    if (!this.enabled) return;
    const btn = e.target.closest?.('.du-btn');
    if (btn) {
      // Buttons are handled here, not by the look area: a thumb on FIRE must
      // not also drag the camera.
      btn.classList.add('press');
      this.input.setTouch(btn.dataset.act, true);
      e.preventDefault();
      return;
    }
    if (this._stickId === null && e.clientX < innerWidth * 0.42) {
      this._stickId = e.pointerId;
      this._roles.set(e.pointerId, 'stick');
      this._stickOrigin.x = e.clientX;
      this._stickOrigin.y = e.clientY;
      this.stick.style.left = `${e.clientX}px`;
      this.stick.style.top = `${e.clientY}px`;
      this.stick.classList.add('on');
      this._moveKnob(0, 0);
      e.preventDefault();
      return;
    }
    if (this._lookId === null) {
      this._lookId = e.pointerId;
      this._roles.set(e.pointerId, 'look');
      this._lookLast.x = e.clientX;
      this._lookLast.y = e.clientY;
      e.preventDefault();
    }
  }

  _onMove(e) {
    if (!this.enabled) return;
    const role = this._roles.get(e.pointerId);
    if (role === 'stick') {
      const dx = e.clientX - this._stickOrigin.x;
      const dy = e.clientY - this._stickOrigin.y;
      const len = Math.hypot(dx, dy) || 1;
      const cl = Math.min(1, len / STICK_R);
      const nx = (dx / len) * cl;
      const ny = (dy / len) * cl;
      this._moveKnob(dx * (cl * STICK_R / len), dy * (cl * STICK_R / len));
      // Screen y grows downward, so "up" is negative — which already matches
      // the gamepad convention Input expects (push up = -1 = forward).
      this.input.setTouchMove(nx, ny);
      e.preventDefault();
    } else if (role === 'look') {
      this.input.addTouchLook(
        (e.clientX - this._lookLast.x) * LOOK_SCALE,
        (e.clientY - this._lookLast.y) * LOOK_SCALE
      );
      this._lookLast.x = e.clientX;
      this._lookLast.y = e.clientY;
      e.preventDefault();
    }
  }

  _onUp(e) {
    if (!this.enabled) return;
    const role = this._roles.get(e.pointerId);
    if (role === 'stick') {
      this.input.setTouchMove(0, 0);
      this.stick.classList.remove('on');
      this._stickId = null;
    } else if (role === 'look') {
      this._lookId = null;
    }
    this._roles.delete(e.pointerId);
    // Release every pressed button: pointerup can land outside the element.
    for (const b of this.root.querySelectorAll('.du-btn.press')) {
      b.classList.remove('press');
      this.input.setTouch(b.dataset.act, false);
    }
  }

  _onCancel(e) { this._onUp(e); }

  _moveKnob(dx, dy) {
    this.knob.style.transform = `translate(-50%,-50%) translate(${dx}px,${dy}px)`;
  }

  dispose() {
    this.disposed = true;
    this.enabled = false;
    this.root?.removeEventListener('pointerdown', this._onDown);
    this.root?.removeEventListener('pointermove', this._onMove);
    removeEventListener('pointerup', this._onUp);
    removeEventListener('pointercancel', this._onCancel);
    this.root?.remove();
    this.input?.setTouchMove(0, 0);
  }
}
